/**
 * localStorage-backed session/preferences store for the guest-app.
 * Mirrors the saas-admin Storage: a single flat JSON blob under one key.
 * The guest-app is multi-tenant by subdomain, but the *session* token is
 * still a single value (one device → one guest stay at a time).
 */
export class Storage
{
    static STORAGE_ID = 'belturo-guest-app-storage';

    // Note: This needs to be a flat data structure.
    static DEFAULT_DATA = {
        "sessionToken" : null,
        // Last known forecast + when it was stored (see core/Weather.js), so the
        // home screen paints instantly and survives an offline app open.
        "weather" : null,
        // Which trip plan the Planer tab reopens on. A guest may keep several
        // (see core/Planner.js); remembering the last one is what makes coming
        // back to the tab land where they left rather than on a picker.
        "activePlanId" : null,
        // null = never chosen; the app then falls back to the phone's language
        // (see i18n/resolveLanguage).
        "language" : null,
        // Survey answers not yet confirmed by the server, per survey token:
        // { <token>: { <questionId>: value } }. A guest answering on a bad
        // connection loses nothing (see states/survey/SurveyState).
        "surveyQueue" : null,
        // How often the app was opened, how often the "install the app" hint
        // showed, and whether the guest closed it (see core/Pwa.js).
        "pwaVisits" : 0,
        "pwaInstallHintShows" : 0,
        "pwaInstallHintDismissed" : false
    };

    saveLanguage( langCode )
    {
        this.save( "language", langCode );
    }

    loadLanguage()
    {
        return this.load( "language" );
    }

    saveSessionToken( sessionToken )
    {
        this.save( "sessionToken", sessionToken );
    }

    loadSessionToken()
    {
        return this.load( "sessionToken" );
    }

    deleteSessionToken()
    {
        this.save( "sessionToken", null );
    }

    load( key, defVal = null )
    {
        const sd = this.loadStorageData();
        if ( sd.hasOwnProperty( key ) )
        {
            return sd[ key ];
        }
        else
        {
            return defVal;
        }
    }

    save( key, val )
    {
        const sd = this.loadStorageData();
        sd[ key ] = val;
        this.saveStorageData( sd );
    }

    saveStorageData( updatedSd )
    {
        localStorage.setItem( Storage.STORAGE_ID, JSON.stringify( updatedSd ) );
    }

    loadStorageData()
    {
        let sd = localStorage.getItem( Storage.STORAGE_ID );
        if ( sd && sd.length )
        {
            try
            {
                sd = JSON.parse( sd );
            }
            catch( e )
            {
                sd = {};
            }
        }
        return { ...Storage.DEFAULT_DATA, ...sd };
    }
}
